import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

const STORAGE_KEY = 'wishlist:items';
const RENDER_SECTION_ID = 'recently-carousel-slide_original';
const RENDER_CARD_SELECTOR = 'product-card';

// 連打を1リクエストに畳み込むためのデバウンス時間。
// Admin APIのコストとCloud Run課金の両方を抑える目的。
const WISHLIST_DEBOUNCE_MS = 400;

/**
 * 未ログインユーザー用 localStorage ストア。
 * `[{ id, handle }, ...]` を保存し、ハート押下や一覧描画から参照される。
 */
class WishlistStore {
  /** @returns {{id: string, handle: string}[]} */
  static getItems() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      const data = JSON.parse(raw);
      if (!Array.isArray(data)) return [];
      return data.filter((item) => item && typeof item.id === 'string' && typeof item.handle === 'string');
    } catch {
      return [];
    }
  }

  /** @param {{id: string, handle: string}[]} items */
  static setItems(items) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }

  /** @returns {string[]} */
  static getIds() {
    return this.getItems().map((item) => item.id);
  }

  /** @returns {string[]} */
  static getHandles() {
    return this.getItems().map((item) => item.handle);
  }

  /** @returns {number} */
  static count() {
    return this.getItems().length;
  }

  /** @param {string} id */
  static has(id) {
    return this.getItems().some((item) => item.id === id);
  }

  /**
   * @param {string} id
   * @param {string} handle
   */
  static add(id, handle) {
    if (!id || !handle) return;
    const items = this.getItems();
    if (items.some((item) => item.id === id)) return;
    items.unshift({ id, handle });
    this.setItems(items);
  }

  /** @param {string} id */
  static remove(id) {
    const items = this.getItems().filter((item) => item.id !== id);
    this.setItems(items);
  }

  static clear() {
    localStorage.removeItem(STORAGE_KEY);
  }
}

/** ページ内のすべてのボタン/バッジに状態を伝播するヘルパ。 */
function dispatchWishlistUpdate(productIds, count) {
  document.dispatchEvent(
    new CustomEvent(ThemeEvents.wishlistUpdate, {
      detail: { productIds, count },
    }),
  );
}

/**
 * お気に入りトグルボタン。
 *
 * - 未ログイン: localStorage に保存し、楽観的UIで状態反転
 * - ログイン済: 楽観的UI でアクティブ状態を即時反転し、App Proxy 経由で /add か /remove を叩く
 * - レスポンスで返る productIds を wishlist:update イベントに載せ、ページ内の全ボタン/バッジが追従する
 *
 * @extends {Component<{}>}
 */
class WishlistToggleButton extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('click', this.#onClick);
    document.addEventListener(ThemeEvents.wishlistUpdate, this.#onWishlistUpdate);

    // 未ログイン時は localStorage から初期状態を復元
    if (!this.#isLoggedIn) {
      this.#isActive = WishlistStore.has(this.#productId);
    }
    // サーバー反映済み状態の初期値。連打後に元に戻った場合、サーバーを叩かず済ませるため。
    this.#serverState = this.#isActive;
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#onClick);
    document.removeEventListener(ThemeEvents.wishlistUpdate, this.#onWishlistUpdate);
    if (this.#debounceTimer != null) {
      clearTimeout(this.#debounceTimer);
      this.#debounceTimer = null;
    }
  }

  get #productId() {
    return this.dataset.productId ?? '';
  }

  get #productHandle() {
    return this.dataset.productHandle ?? '';
  }

  get #isActive() {
    return this.dataset.active === 'true';
  }

  set #isActive(value) {
    this.dataset.active = value ? 'true' : 'false';
    this.#updateLabel(value);
  }

  #updateLabel(active) {
    const label = this.querySelector('.wishlist-toggle__label');
    if (!label) return;
    const text = active ? this.dataset.labelRemove : this.dataset.labelAdd;
    if (text) label.textContent = text;
    const btn = this.querySelector('.wishlist-toggle__button');
    if (btn) btn.setAttribute('aria-label', text || '');
  }

  get #isLoggedIn() {
    return this.dataset.loggedIn === 'true';
  }

  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {ReturnType<typeof setTimeout> | null} */
  #debounceTimer = null;

  /**
   * 最後にサーバーに反映された（と信じている）状態。
   * クリックで即時反転する `#isActive` と比較し、連打で元に戻ったら何も送信しない。
   */
  #serverState = false;

  /**
   * @param {Event} event
   */
  #onClick = (event) => {
    event.preventDefault();
    event.stopPropagation();

    const wasActive = this.#isActive;

    // 未ログイン: localStorage への書き込みはコストゼロなのでデバウンスせず即時反映
    if (!this.#isLoggedIn) {
      if (wasActive) {
        WishlistStore.remove(this.#productId);
      } else {
        WishlistStore.add(this.#productId, this.#productHandle);
        this.#trackAddToWishlist();
      }
      this.#isActive = !wasActive;
      dispatchWishlistUpdate(WishlistStore.getIds(), WishlistStore.count());
      return;
    }

    // ログイン済: 楽観的UIで即時反転し、サーバー送信は debounce で畳み込む
    this.#isActive = !wasActive;
    this.dataset.pending = 'true';

    // 進行中の sync をキャンセル（新しい意図で上書き）
    this.#abortController?.abort();
    this.#abortController = null;

    if (this.#debounceTimer != null) clearTimeout(this.#debounceTimer);
    this.#debounceTimer = setTimeout(() => {
      this.#debounceTimer = null;
      void this.#sync();
    }, WISHLIST_DEBOUNCE_MS);
  };

  async #sync() {
    const desired = this.#isActive;
    // サーバー状態と一致（連打で元に戻った）→ 何も送らない
    if (desired === this.#serverState) {
      this.dataset.pending = 'false';
      return;
    }

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    const endpoint = desired
      ? '/apps/mood-storefront/wishlist/add'
      : '/apps/mood-storefront/wishlist/remove';

    try {
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ productId: this.#productId }),
        signal,
      });
      if (!res.ok) throw new Error(`Wishlist ${endpoint} failed: ${res.status}`);
      const data = /** @type {{ productIds: string[], count: number }} */ (await res.json());
      this.#serverState = desired;
      if (desired) this.#trackAddToWishlist();
      dispatchWishlistUpdate(data.productIds, data.count);
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      console.error('[wishlist] sync failed', error);
      // ロールバック: サーバー状態に戻す
      this.#isActive = this.#serverState;
    } finally {
      // 次の debounce が入っていない場合のみ pending 解除（入っていれば次のサイクルで解除される）
      if (this.#debounceTimer == null) {
        this.dataset.pending = 'false';
      }
    }
  }

  #trackAddToWishlist() {
    if (typeof window.gtag !== 'function') return;
    // GA4 / V4C 整合のため、Shopify Sales Channel と同じ
    // shopify_JP_{product_id}_{variant_id} 形式の id を送る (data-ga-item-id)
    const gaItemId = this.dataset.gaItemId || this.#productId;
    window.gtag('event', 'add_to_wishlist', {
      send_to: 'G-JTQB19HCBY',
      item_list_name: this.dataset.location || 'unknown',
      items: [{ item_id: gaItemId }],
    });
  }

  /**
   * 他のボタンからのトグルに追従する。
   * 自身がリクエスト中(pending)なら楽観的状態を維持し、外部イベントで上書きしない。
   * @param {CustomEvent<{ productIds: string[], count: number }>} event
   */
  #onWishlistUpdate = (event) => {
    if (this.dataset.pending === 'true') return;
    const ids = event.detail?.productIds;
    if (!Array.isArray(ids)) return;
    const pid = this.#productId;
    if (!pid) return;
    const shouldBeActive = ids.some((id) => id === pid || id.endsWith(`/${pid}`));
    this.#isActive = shouldBeActive;
    this.#serverState = shouldBeActive;
  };
}

if (!customElements.get('wishlist-toggle-button')) {
  customElements.define('wishlist-toggle-button', WishlistToggleButton);
}

/**
 * ヘッダーに置くカウントバッジ付きアイコン。
 * 未ログイン時は localStorage から件数 hydrate、wishlist:update イベントで件数を更新する。
 *
 * @extends {Component<{ count: HTMLElement }>}
 */
class WishlistIcon extends Component {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.wishlistUpdate, this.#onWishlistUpdate);

    // 未ログイン時は localStorage から件数を hydrate
    if (this.dataset.loggedIn !== 'true') {
      this.#applyCount(WishlistStore.count());
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.wishlistUpdate, this.#onWishlistUpdate);
  }

  /** @param {number} count */
  #applyCount(count) {
    const bubble = this.refs.count;
    if (bubble) {
      const countText = bubble.querySelector('.wishlist-bubble__count');
      if (countText) {
        countText.textContent = count > 0 && count < 100 ? String(count) : '';
      }
      bubble.classList.toggle('visually-hidden', count === 0);
    }
    this.dataset.hasItems = count > 0 ? 'true' : 'false';
    this.classList.toggle('header-actions__wishlist-icon--empty', count === 0);
  }

  /**
   * @param {CustomEvent<{ productIds: string[], count: number }>} event
   */
  #onWishlistUpdate = (event) => {
    const count = event.detail?.count;
    if (typeof count !== 'number') return;
    this.#applyCount(count);
  };
}

if (!customElements.get('wishlist-icon')) {
  customElements.define('wishlist-icon', WishlistIcon);
}

/**
 * 未ログインユーザー向けウィッシュリスト一覧グリッド。
 * localStorage の handle 配列から Section Rendering API で
 * `recently-carousel-slide_original` セクションを fetch し、
 * `.recently-product-card` を抽出して li に包んで挿入する。
 *
 * @extends {Component<{ list: HTMLElement }>}
 */
class WishlistLocalGrid extends Component {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.wishlistUpdate, this.#onWishlistUpdate);
    this.#render();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.wishlistUpdate, this.#onWishlistUpdate);
  }

  #onWishlistUpdate = () => {
    this.#render();
  };

  async #render() {
    const items = WishlistStore.getItems();
    const list = this.refs.list;
    if (!list) return;

    if (items.length === 0) {
      list.replaceChildren();
      return;
    }

    const cards = await Promise.all(items.map((item) => this.#fetchCard(item)));

    const fragment = document.createDocumentFragment();
    cards.forEach((cardEl, index) => {
      if (!cardEl) return;
      const item = items[index];
      const li = document.createElement('li');
      li.className = `product-grid__item product-grid__item--${index}`;
      li.dataset.productId = item.id;
      li.appendChild(cardEl);
      fragment.appendChild(li);
    });
    list.replaceChildren(fragment);
  }

  /**
   * @param {{id: string, handle: string}} item
   * @returns {Promise<Element | null>}
   */
  async #fetchCard(item) {
    try {
      const res = await fetch(`/products/${item.handle}?sections=${RENDER_SECTION_ID}`);
      if (!res.ok) throw new Error(`status ${res.status}`);
      const json = await res.json();
      const html = json[RENDER_SECTION_ID];
      if (typeof html !== 'string') return null;
      const tpl = document.createElement('template');
      tpl.innerHTML = html;
      const card = tpl.content.querySelector(RENDER_CARD_SELECTOR);
      return card ? card.cloneNode(true) : null;
    } catch (error) {
      console.warn(`[wishlist] failed to render card for ${item.handle}`, error);
      return null;
    }
  }
}

if (!customElements.get('wishlist-local-grid')) {
  customElements.define('wishlist-local-grid', WishlistLocalGrid);
}

/**
 * 起動時に「ログイン済み + localStorage に未マージのアイテムあり」を判定し、
 * /apps/mood-storefront/wishlist/merge を叩いてサーバー側メタフィールドにマージする。
 * 成功したら localStorage をクリア。
 *
 * ログイン状態は header の <wishlist-icon data-logged-in="..."> から判定。
 */
async function maybeMergeOnLogin() {
  const items = WishlistStore.getItems();
  if (items.length === 0) return;

  const wishlistIcon = document.querySelector('wishlist-icon');
  const isLoggedIn = wishlistIcon?.dataset.loggedIn === 'true';
  if (!isLoggedIn) return;

  // サーバーは「末尾が最新」で保存する。localStorage は unshift なので
  // items[0] が最新 → サーバー投入前に反転して古→新の並びにする
  const ids = items.map((i) => i.id).reverse();
  try {
    const res = await fetch('/apps/mood-storefront/wishlist/merge', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ productIds: ids }),
    });
    if (!res.ok) throw new Error(`merge failed: ${res.status}`);
    const data = /** @type {{ productIds: string[], count: number }} */ (await res.json());
    WishlistStore.clear();
    dispatchWishlistUpdate(data.productIds, data.count);
  } catch (error) {
    console.error('[wishlist] merge on login failed', error);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', maybeMergeOnLogin, { once: true });
} else {
  maybeMergeOnLogin();
}
